import React from 'react';
import { StateStepper } from '../../../../../components/steppers/state-stepper/StateStepper';
import { StateStepperStep } from '../../../../../components/steppers/state-stepper/StateStepperStep';
import { Trans } from '@lingui/react';
import { TimeInBusiness } from './TimeInBusiness';
import { Alert } from '@startuptools/ui/joy/Alert';
import { Stack } from '@startuptools/ui/joy/Stack';
import { Typography } from '@startuptools/ui/joy/Typography';
import { Box } from '@startuptools/ui/joy/Box';
import { Button } from '@startuptools/ui/joy/Button';
import { DialogContent } from '@startuptools/ui/Dialog';
import { FormControl } from '@startuptools/ui/FormControl';
import { FormLabel } from '@startuptools/ui/FormLabel';
import { Radio } from '@startuptools/ui/joy/Radio';
import { RadioGroup } from '@startuptools/ui/joy/RadioGroup';
import { DialogActions } from '@startuptools/ui/Dialog';
import { RejectState } from './types';
import WarningIcon from '@mui/icons-material/Warning';
import { EmployeeCount } from './EmployeeCount';
import { BalanceSheet } from './BalanceSheet';
import { GovernmentOwnership } from './GovernmentOwnership';
import { Listed } from './Listed';
import { Practice } from './Practice';
import { GqlSeverity } from '../../../../../graphql/base-types.graphql';
import { useNavigate } from '../../../../../components/Navigate';

const completed = { completed: true, hasError: false, severity: GqlSeverity.Warning };

const QesoPrereqTest = ({
  startCreation,
  onClose,
  ...props
}: { onClose: () => void; startCreation: () => void } & Omit<
  React.ComponentProps<typeof TimeInBusiness>,
  'onReject'
>) => {
  const [completedSteps, setCompletedSteps] = React.useState<string[]>([]);
  const [notPossibleState, setNotPossibleState] = React.useState<RejectState | undefined>();
  const [optionKind, setOptionKind] = React.useState<'qeso' | 'warrant'>('warrant');
  const { navigate } = useNavigate();

  if (notPossibleState) {
    return (
      <>
        <DialogContent>
          <Alert sx={{ alignItems: 'flex-start' }} startDecorator={<WarningIcon />} variant="outlined" color="warning">
            <Stack gap={1}>
              <Typography sx={{ color: 'inherit' }}>{notPossibleState.title}</Typography>
              <Typography sx={{ color: 'inherit' }} level="body-sm">
                <Trans
                  id="If {reason} it is possible to issue employee stock options, but they will be taxed as salary upon issue. It is probably more attractive for you to use a warrant program instead, even if this means the people receiving them needs to purchase them. Warrants are taxed when the shares are sold and then as income of capital rather than salary."
                  values={{ reason: notPossibleState.description }}
                />
              </Typography>
            </Stack>
          </Alert>
          <FormControl sx={{ overflow: 'hidden' }}>
            <FormLabel>
              <Trans id="How do you want to continue?" />
            </FormLabel>
            <RadioGroup value={optionKind} onChange={e => setOptionKind(e.target.value as 'qeso' | 'warrant')}>
              <Radio value="warrant" label={<Trans id="Go to warrant programs" />} />
              <Radio value="qeso" label={<Trans id="Continue with options program without beneficial taxation" />} />
            </RadioGroup>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={onClose}>
            <Trans id="Cancel" />
          </Button>
          <Button
            onClick={() => {
              if (optionKind === 'qeso') {
                startCreation();
              } else {
                void navigate('/stock-options/warrant-programs');
              }
            }}
          >
            <Trans id="Continue" />
          </Button>
        </DialogActions>
      </>
    );
  }
  return (
    <Box sx={{ minHeight: '700px', width: { xs: '100%', md: '600px', lg: '800px' } }}>
      <StateStepper onStepSaved={id => setCompletedSteps(prev => [...prev, id])} onFinalStep={startCreation}>
        <StateStepperStep
          validatorStatus={completedSteps.includes('timeInBusiness') ? completed : null}
          label={<Trans id="Business Period" />}
          id="timeInBusiness"
        >
          <TimeInBusiness {...props} onReject={setNotPossibleState} />
        </StateStepperStep>

        <StateStepperStep
          validatorStatus={completedSteps.includes('numberOfEmployee') ? completed : null}
          label={<Trans id="Number of Employees" />}
          id="numberOfEmployee"
        >
          <EmployeeCount onReject={setNotPossibleState} />
        </StateStepperStep>

        <StateStepperStep
          validatorStatus={completedSteps.includes('balanceSheet') ? completed : null}
          label={<Trans id="Balance Sheet" />}
          id="balanceSheet"
        >
          <BalanceSheet onReject={setNotPossibleState} />
        </StateStepperStep>

        <StateStepperStep
          validatorStatus={completedSteps.includes('governmentOwnership') ? completed : null}
          label={<Trans id="Government Ownership" />}
          id="governmentOwnership"
        >
          <GovernmentOwnership onReject={setNotPossibleState} />
        </StateStepperStep>

        <StateStepperStep
          validatorStatus={completedSteps.includes('listed') ? completed : null}
          label={<Trans id="Listed vs Unlisted" />}
          id="listed"
        >
          <Listed onReject={setNotPossibleState} />
        </StateStepperStep>

        <StateStepperStep
          validatorStatus={completedSteps.includes('scopeOfPractice') ? completed : null}
          label={<Trans id="Scope of Practice" />}
          id="scopeOfPractice"
        >
          <Practice onReject={setNotPossibleState} />
        </StateStepperStep>
      </StateStepper>
    </Box>
  );
};

export default QesoPrereqTest;
